import * as React from 'react';

import { addImportedModelsToProject } from 'src/actions/projects/action-creators/addImportedModelsToProject';
import {
  AddModelType,
  useAddModelType,
} from 'src/containers/ProjectModelDialog/hooks/useAddModelTypeValue';
import { useImportModel } from 'src/containers/ProjectModelDialog/hooks/useImportModelValue';
import { useUploadModel } from 'src/containers/ProjectModelDialog/hooks/useUploadModelValue';
import { connectionSelector } from 'src/selectors/connection';
import { PendingModelData } from 'src/store/state/projects';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useAddImportedModels = () => {
  const dispatch = useReduxDispatch();
  const { addModelType } = useAddModelType();
  const { databaseId: importDatabaseId, dataModels: importDataModels } =
    useImportModel();
  const { databaseId: uploadDatabaseId, name: uploadModelName } =
    useUploadModel();
  const { projectId } = useRouterParams();
  const connection = useReduxSelector(connectionSelector);

  const isAddImportedModelsEnabled = React.useMemo(
    () =>
      addModelType === AddModelType.IMPORT
        ? importDatabaseId && Boolean(importDataModels.length)
        : uploadDatabaseId && uploadModelName,
    [
      addModelType,
      importDatabaseId,
      importDataModels,
      uploadDatabaseId,
      uploadModelName,
    ]
  );

  const handleAddImportedModel = React.useCallback(
    async (pendingModelData?: Record<string, PendingModelData>) => {
      if (!connection || !isAddImportedModelsEnabled) {
        return;
      }

      await dispatch(
        addImportedModelsToProject({
          projectId,
          databaseId:
            addModelType === AddModelType.IMPORT
              ? importDatabaseId
              : uploadDatabaseId,
          modelNames:
            addModelType === AddModelType.IMPORT
              ? importDataModels
              : [uploadModelName],
          pendingModelData,
          connection,
        })
      );
    },
    [
      addModelType,
      connection,
      dispatch,
      importDataModels,
      importDatabaseId,
      isAddImportedModelsEnabled,
      projectId,
      uploadDatabaseId,
      uploadModelName,
    ]
  );

  return {
    handleAddImportedModel,
    isAddImportedModelsEnabled,
  };
};
